import styled from '@emotion/styled';
import * as Sentry from '@sentry/browser';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { get, head, isEmpty } from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import {
    CareRecommendationRecommendation,
    CoachingVisitNode,
    IntakeFormQuestionResponseNode,
} from 'src/generated/webapp_gql';
import {
    getMyCoaches,
    getMyProviderAssignments,
    getMyProviders,
    getMyTherapists,
} from 'src/modules/providers/selectors/providersSelectors';
import {
    getAdditionalInfoProvided,
    getUserInformation,
} from 'src/modules/user/selectors/userSelectors';
import {
    getSessionUtilizationInfo,
    getUpcomingVideoSessions,
} from 'src/modules/videoSessions/selectors/videoSessionsSelectors';
import inboxImage from 'src/public/img/inbox.svg';
import { color, font } from 'src/shared/styles/variables';
import {
    GetIntakeFormResponsesQuery,
    useGetIntakeFormResponsesQuery,
} from '../documents/getIntakeFormResponses.generated';
import { getHPIContinuityText } from '../helpers/healthPlanHelpers';
import { IntakeFormReminderButton } from './IntakeFormReminderButton';

import { SessionDetailsModal } from './SessionDetailsModal';

const SessionsListContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 8,
    overflow: 'hidden',
    flex: '1 1 calc(40% - 20px)',
    marginRight: 10,
    marginLeft: 10,
    marginBottom: 10,
    // Ensure the session widget is a reasonable size
    minWidth: 275,
});

const Header = styled.div({
    width: '100%',
    alignSelf: 'center',
    borderRadius: '8px 8px 0px 0px',
    padding: 17,
    backgroundColor: color.royalBlueSaturated,
});

const HeaderTitle = styled.div({
    color: color.white,
    lineHeight: 1.13,
    ...font.black(font.size.medium),
});

const SessionsRemaining = styled.div({
    color: color.white,
    marginTop: 4,
    ...font.medium(font.size.base),
});

const SessionListContainer = styled.div(() => ({
    flexGrow: 1,
    minHeight: 132,
    borderLeft: `2px solid ${color.catskillWhite}`,
    borderRight: `2px solid ${color.catskillWhite}`,
}));

const SessionRowContainer = styled.div({
    padding: 10,
    cursor: 'pointer',
    backgroundColor: color.white,
    borderBottom: `2px solid ${color.catskillWhite}`,
    display: 'flex',
});

export const CalendarIconContainer = styled.div({
    minWidth: 40,
    minHeight: 40,
    display: 'flex',
    flexDirection: 'column',
});

export const CalendarIconMonth = styled.div({
    width: '100%',
    textAlign: 'center',
    paddingTop: 2,
    borderRadius: '4px 4px 0px 0px',
    backgroundColor: color.royalBlueSaturated,
    color: color.white,
    textTransform: 'capitalize',
    ...font.black(font.size.xxSmall),
});

export const CalendarIconDay = styled.div({
    width: '100%',
    textAlign: 'center',
    borderRadius: '0px 0px 4px 4px',
    backgroundColor: color.concrete,
    padding: 2,
    color: color.nightRider,
    ...font.heavy(font.size.base),
});

const SessionInfo = styled.div({
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    marginLeft: 16,
});

const SessionTitle = styled.div({
    color: color.shark,
    ...font.medium(font.size.medium),
    letterSpacing: 0.7,
    fontWeight: 900,
});

const ScheduledTime = styled.div({
    color: color.steelGray,
    ...font.medium(font.size.base),
    letterSpacing: 0.2,
    textTransform: 'capitalize',
});

const ButtonRow = styled.div({
    padding: '10px 16px 10px 16px',
    backgroundColor: color.white,
    borderRadius: '0px 0px 8px 8px',
    borderLeft: `2px solid ${color.catskillWhite}`,
    borderRight: `2px solid ${color.catskillWhite}`,
    borderBottom: `2px solid ${color.catskillWhite}`,
});

const Button = styled.button(
    props => ({
        width: '100%',
        padding: '11px 10px 8px',
        borderRadius: 3,
        border: 'none',
        color: color.white,
        backgroundColor: props.disabled ? color.gullGray : color.tahitiGold,
        cursor: props.disabled ? 'not-allowed' : 'pointer',
    }),
    font.black(font.size.basePlus),
);

const NoScheduledSessionsContainer = styled.div({
    padding: 14,
    backgroundColor: color.white,
    textAlign: 'center',
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexWrap: 'nowrap',
    flexDirection: 'column',
    borderBottom: `2px solid ${color.catskillWhite}`,
    borderLeft: `2px solid ${color.catskillWhite}`,
    borderRight: `2px solid ${color.catskillWhite}`,
});

const NoScheduledSessionsTitle = styled.div({
    marginTop: 10,
    marginBottom: 5,
    color: color.nightRider,
    ...font.medium(font.size.medium),
    fontWeight: 900,
});

const NoScheduledSessionsAvailable = styled.div({
    color: color.steelGray,
    ...font.medium(font.size.base),
});

const InboxIcon = styled.img({
    width: 100,
    height: 50,
});

interface SessionRowProps {
    session: CoachingVisitNode;
    onClick: () => void;
}

export const calculateResponseCount = (data?: GetIntakeFormResponsesQuery) => {
    const intakeFormQuestionResponses = data?.intakeForm
        ?.intakeFormQuestionResponses as IntakeFormQuestionResponseNode[];
    let completedResponseCount = 0;
    let emptyResponseCount = 0;

    if (!intakeFormQuestionResponses) {
        return { completedResponseCount, emptyResponseCount };
    }

    intakeFormQuestionResponses.forEach(({ questionResponseContent }) => {
        if (!questionResponseContent || questionResponseContent.length === 0) {
            emptyResponseCount += 1;
        } else {
            completedResponseCount += 1;
        }
    });

    return {
        completedResponseCount,
        emptyResponseCount,
    };
};

const SessionRow: React.FC<SessionRowProps> = ({ session, onClick }) => {
    const { t } = useTranslation();
    const coachName = get(session, 'coachAssignment.coach.firstName');

    if (!coachName) {
        return null;
    }

    const scheduledTime = moment(session.scheduledTime);

    return (
        <SessionRowContainer onClick={onClick} data-testid={session.uuid}>
            <CalendarIconContainer>
                <CalendarIconMonth>{scheduledTime.format('MMM')}</CalendarIconMonth>
                <CalendarIconDay>{scheduledTime.format('D')}</CalendarIconDay>
            </CalendarIconContainer>
            <SessionInfo>
                <SessionTitle>
                    {t('videoSessions:sessions_widget.session_with', { name: coachName })}
                </SessionTitle>
                <ScheduledTime>
                    {scheduledTime.format('dddd')}, {scheduledTime.format('MMM D • h:mm A')}
                </ScheduledTime>
            </SessionInfo>
        </SessionRowContainer>
    );
};

const NoScheduledSessions: React.FC<{ visitsRemaining: number; sessionType: string }> = ({
    visitsRemaining: num,
    sessionType,
}) => {
    const { t } = useTranslation();
    return (
        <NoScheduledSessionsContainer>
            <InboxIcon src={inboxImage} />
            <NoScheduledSessionsTitle>
                {t('videoSessions:sessions_widget.no_sessions')}
            </NoScheduledSessionsTitle>
            <NoScheduledSessionsAvailable>
                {t(
                    `videoSessions:sessions_widget.session_available${
                        sessionType ? `.${sessionType.toLowerCase()}` : ''
                    }`,
                    { count: num },
                )}
            </NoScheduledSessionsAvailable>
        </NoScheduledSessionsContainer>
    );
};

export enum SessionsWidgetTestID {
    TherapySessionsLeft = 'SessionsWidgetTestID.TherapySessionsLeft',
    CoachingSessionsLeft = 'SessionsWidgetTestID.CoachingSessionsLeft',
    MainButton = 'SessionsWidgetTestID.MainButton',
}

export enum SessionsStatus {
    NotMatched = 'NotMatched',
    NotScheduled = 'NotScheduled',
    Scheduled = 'Scheduled',
}

export const SessionsWidget: React.FC = () => {
    const { t } = useTranslation();
    const { healthPlanIntegrationMemberEligibility, showIntakeForm } = useFlags();
    const [selectedSession, setSelectedSession] = useState<CoachingVisitNode | null>(null);

    const history = useHistory();
    const user = useSelector(getUserInformation);
    const carePreference =
        head(user.careRecommendations)?.recommendation || CareRecommendationRecommendation.Coaching;

    const [emptyResponses, setEmptyResponses] = useState(0);
    const [completedResponses, setCompletedResponses] = useState(0);
    const upcomingSessions = useSelector(getUpcomingVideoSessions);
    const myProviders = useSelector(getMyProviders);
    const myCoaches = useSelector(getMyCoaches);
    const myTherapists = useSelector(getMyTherapists);
    const additionalInfoProvided = useSelector(getAdditionalInfoProvided);
    const { coachingVisitsRemaining, therapyVisitsRemaining, resetDate } = useSelector(
        getSessionUtilizationInfo,
    );
    const myProviderAssignments = useSelector(getMyProviderAssignments);

    let originallyAssignedTherapy = false;

    const getCoachAssignmentFromCoachUuid = (providerUuid: string) => {
        return myProviderAssignments.find(({ coach }) => providerUuid === coach?.uuid)?.uuid || '';
    };

    const getSessionType = () => {
        // if you have no providers just go with the care recommendation
        if (!myProviders?.length) {
            return carePreference === CareRecommendationRecommendation.Therapy
                ? 'Therapy'
                : carePreference === CareRecommendationRecommendation.MentalHealthCoaching
                ? 'Mental_health_coaching'
                : 'Coaching';
        }

        // if you are matched to a therapist and have visits show the therapy widget
        if (myTherapists.length && therapyVisitsRemaining) {
            return 'Therapy';
        }

        // if you are matched to a coach and have visits show the coach widget
        if (myCoaches.length && coachingVisitsRemaining) {
            return 'Coaching';
        }

        // if you are originally matched to a therapist and have no more therapy visits show the coach widget
        if (myTherapists.length && !therapyVisitsRemaining && coachingVisitsRemaining) {
            originallyAssignedTherapy = true;
            return 'Coaching';
        }

        // if you are originally matched to a coach (and are matched with a therapist) and have no more coaching visits show the therapy widget
        if (
            myCoaches.length &&
            myTherapists.length &&
            !coachingVisitsRemaining &&
            therapyVisitsRemaining
        ) {
            return 'Therapy';
        }

        // if you are out of all your visits and matched to therapist show the out of sessions ui widget
        if (myTherapists.length && !coachingVisitsRemaining && !therapyVisitsRemaining) {
            return 'Therapy';
        }

        return carePreference === CareRecommendationRecommendation.Therapy ? 'Therapy' : 'Coaching';
    };

    const sessionType = getSessionType();

    const getSessionStatus = () => {
        if (!(sessionType === 'Therapy' ? myTherapists.length : myCoaches.length)) {
            return SessionsStatus.NotMatched;
        }

        if (upcomingSessions.length) {
            return SessionsStatus.Scheduled;
        }

        if (!upcomingSessions.length) {
            return SessionsStatus.NotScheduled;
        }

        return SessionsStatus.NotMatched;
    };

    const sessionsStatus: SessionsStatus = getSessionStatus();

    const outOfSessions: boolean =
        myTherapists.length && myCoaches.length
            ? therapyVisitsRemaining + coachingVisitsRemaining == 0
            : sessionType === 'Therapy'
            ? therapyVisitsRemaining === 0
            : coachingVisitsRemaining === 0;

    const qualifiesForHPISessions: boolean =
        healthPlanIntegrationMemberEligibility &&
        get(user, 'visitsRemaining.therapyVisitsRemaining') <= 0 &&
        user.hpiMemberInsurance !== null;

    let headerText;
    let buttonText;
    let buttonDisabled;

    switch (sessionsStatus) {
        case SessionsStatus.NotMatched:
            const providerText =
                sessionType === 'Therapy'
                    ? 'therapist'
                    : sessionType === 'Mental_health_coaching'
                    ? 'mental_health_coach'
                    : 'coach';

            if (originallyAssignedTherapy) {
                headerText = t('videoSessions:sessions_widget.originally_assigned_therapy.header');
                buttonText = t(`videoSessions:sessions_widget.select.${providerText}`);
                buttonDisabled = false;
                break;
            }

            if (outOfSessions) {
                headerText = t('videoSessions:sessions_widget.out_of_sessions.header');
                buttonText = t(`videoSessions:sessions_widget.select.${providerText}`);
                buttonDisabled = true;
                break;
            }

            headerText = t(`videoSessions:sessions_widget.header.${providerText}`);
            buttonText = t(`videoSessions:sessions_widget.select.${providerText}`);
            buttonDisabled = false;
            break;

        case SessionsStatus.NotScheduled:
            if (outOfSessions && qualifiesForHPISessions) {
                const { hText, bText, bDisabled } = getHPIContinuityText(
                    user.hpiEligibilityStatus,
                    resetDate.format('MMM Do, YYYY'),
                    t,
                );
                headerText = hText;
                buttonText = bText;
                buttonDisabled = bDisabled;
                break;
            }

            if (outOfSessions) {
                headerText = t('videoSessions:sessions_widget.out_of_sessions.header');
                buttonText = t('videoSessions:sessions_widget.button.schedule_session');
                buttonDisabled = true;
                break;
            }

            headerText = t('videoSessions:sessions_widget.header.schedule_session');
            buttonText = t('videoSessions:sessions_widget.button.schedule_session');
            buttonDisabled = false;
            break;

        case SessionsStatus.Scheduled:
            if (outOfSessions && qualifiesForHPISessions) {
                const { hText, bText, bDisabled } = getHPIContinuityText(
                    user.hpiEligibilityStatus,
                    resetDate.format('MMM Do, YYYY'),
                    t,
                );
                headerText = hText;
                buttonText = bText;
                buttonDisabled = bDisabled;
                break;
            }

            if (outOfSessions) {
                headerText = t('videoSessions:sessions_widget.out_of_sessions.header');
                buttonText = t('videoSessions:sessions_widget.button.schedule_session');
                buttonDisabled = true;
                break;
            }

            headerText = t('videoSessions:sessions_widget.header.upcoming_session', {
                count: upcomingSessions.length,
            });
            buttonText = t('videoSessions:sessions_widget.button.schedule_session');
            buttonDisabled = false;
            break;

        default:
            headerText = '';
            buttonText = '';
            buttonDisabled = true;
    }

    const getSessionsLeftParagraph = (type: 'coaching' | 'therapy') => {
        const resetDateFormatted = resetDate.format('MMM Do, YYYY');

        const num = type === 'coaching' ? coachingVisitsRemaining : therapyVisitsRemaining;

        return t(`videoSessions:sessions_widget.session_available_until.${type}`, {
            count: num,
            date: resetDateFormatted,
        });
    };

    const handleButtonClick = () => {
        if (buttonDisabled) {
            return;
        }

        const screen =
            sessionType === 'Coaching'
                ? '/work-with-a-coach'
                : sessionType === 'Mental_health_coaching'
                ? '/work-with-a-mental-health-coach'
                : '/work-with-a-therapist';

        const nextScreen =
            (sessionType === 'Therapy' ? myTherapists.length : myCoaches.length) == 0
                ? screen
                : '/schedule-session/select-coach';

        if (additionalInfoProvided) {
            history.push(nextScreen);
        } else {
            history.push('/provide-additional-info', {
                nextScreen,
            });
        }
    };

    const handleIntakeFormNavigate = (coachAssignmentUuid: string) => {
        history.push(`/care-form/${coachAssignmentUuid}`);
    };

    const getCoachAssignmentForIntakeForm = (sessions: CoachingVisitNode[]) => {
        if (sessionType === 'Therapy') {
            return '';
        }

        if (!sessions || isEmpty(sessions)) {
            const [coach] = myCoaches;

            return coach ? getCoachAssignmentFromCoachUuid(coach.uuid) : '';
        }

        const [nextSession] = sessions;

        return getCoachAssignmentFromCoachUuid(nextSession.coachAssignment?.coach?.uuid) || '';
    };

    const setDontShowIntakeForm = () => {
        setEmptyResponses(0);
        setCompletedResponses(0);
    };

    const intakeFormCoachAssignmentUuid = getCoachAssignmentForIntakeForm(upcomingSessions) || null;
    const { data, error, loading } = useGetIntakeFormResponsesQuery({
        fetchPolicy: 'network-only',
        variables: {
            intakeFormCoachAssignmentUuid,
        },
    });

    useEffect(() => {
        if (loading) {
            return;
        }

        if (data?.intakeForm?.submitted) {
            setDontShowIntakeForm();
            return;
        }

        try {
            if (error) {
                throw error;
            }

            const { completedResponseCount, emptyResponseCount } = calculateResponseCount(data);

            setCompletedResponses(completedResponseCount);
            setEmptyResponses(emptyResponseCount);
        } catch (err) {
            setDontShowIntakeForm();

            if (showIntakeForm) {
                err.name = 'Error retrieving or calculating response counts on intake form';
                Sentry.captureException(err);
            }
        }
    }, [data, error, loading]);

    const shouldShowIntakeReminder = emptyResponses || completedResponses;

    return (
        <>
            <SessionsListContainer>
                <Header>
                    <HeaderTitle>{headerText}</HeaderTitle>
                    {myCoaches.length > 0 ? (
                        <SessionsRemaining data-testid={SessionsWidgetTestID.CoachingSessionsLeft}>
                            {getSessionsLeftParagraph('coaching')}
                        </SessionsRemaining>
                    ) : null}

                    {myTherapists.length > 0 ? (
                        <SessionsRemaining data-testid={SessionsWidgetTestID.TherapySessionsLeft}>
                            {getSessionsLeftParagraph('therapy')}
                        </SessionsRemaining>
                    ) : null}
                </Header>
                {upcomingSessions.length ? (
                    <SessionListContainer>
                        {!!shouldShowIntakeReminder && (
                            <IntakeFormReminderButton
                                nextSession={upcomingSessions[0]}
                                completedResponses={completedResponses}
                                emptyResponses={emptyResponses}
                                coachAssignmentUuid={intakeFormCoachAssignmentUuid}
                                wrapped={true}
                                navigateByCoachAssignment={handleIntakeFormNavigate}
                            />
                        )}
                        {upcomingSessions
                            .slice(0, shouldShowIntakeReminder ? 2 : 3)
                            .map(session => (
                                <SessionRow
                                    key={session.uuid}
                                    session={session}
                                    onClick={() => setSelectedSession(session)}
                                />
                            ))}
                    </SessionListContainer>
                ) : (
                    <>
                        {!!shouldShowIntakeReminder && (
                            <IntakeFormReminderButton
                                emptyResponses={emptyResponses}
                                completedResponses={completedResponses}
                                coachAssignmentUuid={intakeFormCoachAssignmentUuid}
                                wrapped={false}
                                navigateByCoachAssignment={handleIntakeFormNavigate}
                            />
                        )}
                        <NoScheduledSessions
                            sessionType={myTherapists.length && myCoaches.length ? '' : sessionType}
                            visitsRemaining={
                                myTherapists.length && myCoaches.length
                                    ? therapyVisitsRemaining + coachingVisitsRemaining
                                    : sessionType === 'Therapy'
                                    ? therapyVisitsRemaining
                                    : coachingVisitsRemaining
                            }
                        />
                    </>
                )}
                <ButtonRow>
                    <Button
                        onClick={handleButtonClick}
                        disabled={buttonDisabled}
                        data-testid={SessionsWidgetTestID.MainButton}
                    >
                        {buttonText}
                    </Button>
                </ButtonRow>
            </SessionsListContainer>

            {selectedSession ? (
                <SessionDetailsModal
                    visible={!!selectedSession}
                    session={selectedSession}
                    onClose={() => setSelectedSession(null)}
                />
            ) : null}
        </>
    );
};
